/*
 * Copyright (c) 2026 GS Elektromedizinische Geraete G. Stemple GmbH, Germany - all rights reserved.
 * <p>
 * DO NOT ALTER OR REMOVE COPYRIGHT NOTICES OR THIS FILE HEADER.
 */

import { useContext, useState } from 'react';
import PageHeader from '../../customs/DiviPage/PageHeader/PageHeader';
import DiviPage from '../../customs/DiviPage/DiviPage';
import PageFooter from '../../customs/DiviPage/PageFooter/PageFooter';
import { useCSS } from '../../../provider/CSSProvider';
import style from '../Handover/Handover.scss?inline';
import { DiviNavigationIds } from '../../../models/diviProgress';
import { NodeType } from '../../../backendModels/report.model';
import { getPageTitle, PageType } from '../../../models/diviPage';
import Gcs from '../Neurology/Gcs/Gcs';
import Breathing from '../InitialFindings/Breathing/Breathing';
import Consciousness from '../Neurology/Consciousness/Consciousness';
import Ecg from '../InitialFindings/Ecg/Ecg';
import VitalParameter from '../InitialFindings/VitalParameter/VitalParameter';
import Pain from '../InitialFindings/Pain/Pain';
import { BaseNodeRecordValueTypeMap } from '../../../backendModels/records.model';
import { ReportsAPIContext } from '../../../provider/ReportsAPIProvider';
import dayjs from 'dayjs';
import TimeDialog from '../../customs/timeDialog/TimeDialog';
import { BLUTZUCKER_ERWEITERT_ELEMENT_KEY } from '../../../models/genericElements/bloodGlucose';
import { VITALPARAMETER_ERWEITERT_ELEMENT_KEY } from '../../../models/genericElements/vitalParameter';
import { LocalStorageContext } from '../../../provider/LocalStorageProvider';
import { DIVI_VISITED_STATE_STORAGE_KEY, DiviVisitedIds, DiviVisitedStatesType } from '../../../models/diviVisited';
import _ from 'lodash';

export function Handover() {
  useCSS(style);

  const recordTypesOfPage = [
    'gcs',
    'pain',
    'ecgFindings',
    'breathing',
    'consciousness',
    'vitalParameters',
    'circulation',
  ] as (keyof BaseNodeRecordValueTypeMap)[];
  const elementKeysOfPage = [BLUTZUCKER_ERWEITERT_ELEMENT_KEY, VITALPARAMETER_ERWEITERT_ELEMENT_KEY];

  const nodeTypeOfPage = NodeType.HANDOVER;

  const localStorage = useContext(LocalStorageContext);
  const [state, setState] = localStorage.asState<DiviVisitedStatesType>(DIVI_VISITED_STATE_STORAGE_KEY);

  const [timeDialogOpen, setTimeDialogOpen] = useState<boolean>(!state?.includes(DiviVisitedIds.handover));
  const reportAPI = useContext(ReportsAPIContext);
  const timestamp = dayjs(reportAPI.getNodeTimestamp(nodeTypeOfPage) ?? undefined);

  function setTimestamp(newTimestamp: dayjs.Dayjs) {
    reportAPI.setNodeTimestamp(newTimestamp.valueOf(), nodeTypeOfPage);
  }

  const onTimeDialogVisibilityChange = (visible: boolean) => {
    setTimeDialogOpen(visible);

    if (!visible) {
      setState((oldData) => {
        return _.union(oldData, [DiviVisitedIds.handover]);
      });
    }
  };

  function openTimeDialog() {
    setTimeDialogOpen(true);
  }

  const pageHeader = (
    <PageHeader
      heading={getPageTitle(PageType.Handover)}
      onTimeClicked={openTimeDialog}
      time={timestamp.format('HH:mm')}
    />
  );

  //ElementKeys are not passed to footer in order to ensure the no path button highlighting to function
  const pageFooter = (
    <PageFooter
      pageType={PageType.Handover}
      diviId={DiviNavigationIds.Handover}
      noPathButton
      recordTypes={recordTypesOfPage}
      nodeType={nodeTypeOfPage}
    />
  );

  return (
    <DiviPage
      pageHeader={pageHeader}
      pageFooter={pageFooter}
      recordTypes={recordTypesOfPage}
      elementKeys={elementKeysOfPage}
      nodeType={nodeTypeOfPage}
      diviId={DiviNavigationIds.Handover}>
      <TimeDialog
        timestamp={{ timestamp: timestamp, setTimestamp: setTimestamp }}
        dialogVisible={{ open: timeDialogOpen, setOpen: onTimeDialogVisibilityChange }}
      />
      <div className='wireframe-container-handover'>
        <div className='gcs'>
          <Gcs nodeType={nodeTypeOfPage} />
        </div>
        <div className='pain'>
          <Pain nodeType={nodeTypeOfPage} />
        </div>
        <div className='vital-params'>
          <VitalParameter nodeType={nodeTypeOfPage} />
        </div>
        <div className='ecg'>
          <Ecg nodeType={nodeTypeOfPage} />
        </div>
        <Breathing nodeType={nodeTypeOfPage} />
        <Consciousness nodeType={nodeTypeOfPage} />
      </div>
    </DiviPage>
  );
}
